import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import { DateTime } from 'luxon';
import { COLORS } from '@bcm/constants';
import { useNavigation } from '@react-navigation/native';

import { BURN_BSA_TYPES } from '../utils/shared/lundBrowderUtils';
import thickness from '../utils/canvasThickness';
import { calcDimensionsToFillContainer, urlToB64 } from '../../../src/imageUtils';
import { ALERT_TYPES, MODAL_TYPES, USER_TYPES } from '../../../constants/UIConstants';
import CanvasDraw from '../utils/web/canvasUtils';
import BodySiteCanvasLR from './BodySiteCanvasLR';
import { bodySiteCanvasDims } from './bodySiteCanvasUtils';
import BodySiteCanvasFlipButton from './BodySiteCanvasFlipButton';
import BodySiteCanvasColorButtonContainer from './BodySiteCanvasColorButtonContainer';
import BodySiteCanvasFooter from './BodySiteCanvasFooter';
import navbar from '../../UI/styles/navbar';
import BodySiteCanvasBack from './BodySiteCanvasBack';
import LoadingWheel from '../../UI/LoadingWheel';
import { FormText } from '../../../constants/Text';
import Alert from '../../UI/modal/Alert';
import { SILHOUETTE_TYPES, getAgeFromEocCondition, getSilhouette } from '../../../src/silhouetteUtils';
import HeaderLeft from '../../UI/headerButton/HeaderLeft';
import useBackHandler from '../../UI/utils/useBackHandler';

const canvasPadding = 20;
const initData = {
	tbsa: {},
	bodySites: {},
};

const BodySiteCanvas = ({
	onClose,
	modalType,
	analysisDataFront,
	analysisDataBack,
	setAnalysisDataFront,
	setAnalysisDataBack,
	tbsaFormula,
	fluidResuscitationFormula,
	userType,
	eoc,
	condition,
	bodySite,
	isLandscape,
	save,
	update,
	setIsDirty,
	isNavBar,
}) => {

	const layout = useWindowDimensions();
	const frontCanvasRef = useRef();
	const backCanvasRef = useRef();
	const navigation = useNavigation();

	const [isLoading, setIsLoading] = useState(true);
	const [cancelAlertVisible, setCancelAlertVisible] = useState(false);
	const [isDirtyLocal, setIsDirtyLocal] = useState(false);
	const [frontSource, setFrontSource] = useState();
	const [backSource, setBackSource] = useState();
	const [brushColor, setBrushColor] = useState(BURN_BSA_TYPES.burnPartialThickness.color);
	const [brushRadius, setBrushRadius] = useState(thickness[2].value);
	const [isEraser, setIsEraser] = useState(false);
	const [isFront, setIsFront] = useState(true);
	const [initialDataFront] = useState(analysisDataFront);
	const [initialDataBack] = useState(analysisDataBack);
	const [frontData, setFrontData] = useState(initData);
	const [backData, setBackData] = useState(initData);

	const canvasDims = useMemo(() => {
		const dims = calcDimensionsToFillContainer(
			layout.height - bodySiteCanvasDims.footerHeight - navbar.height - canvasPadding,
			layout.width,
			bodySiteCanvasDims.height,
			bodySiteCanvasDims.width,
			false
		);
		return {
			height: dims.maxHeight,
			width: dims.maxWidth,
		};
	}, [layout]);

	const sources = useMemo(() => {
		const res = {
			base: null,
			outline: null,
		};
		if(eoc && condition) {
			let years;
			if (modalType !== MODAL_TYPES.create && bodySite && bodySite.ageCategory) years = bodySite.ageCategory;
			else years = getAgeFromEocCondition(eoc, condition);
			const side = isFront ? 'front' : 'back';
			res.outline = getSilhouette(years, side, SILHOUETTE_TYPES.outline);
			res.base = getSilhouette(years, side, SILHOUETTE_TYPES.base);
		}
		return res;
	}, [eoc, condition, bodySite, isFront, modalType]);

	const canvasClear = () => {
		if (isFront) {
			frontCanvasRef.current.saveScreen();
			frontCanvasRef.current.resetCanvasData(sources.base);
		}
		else {
			backCanvasRef.current.saveScreen();
			backCanvasRef.current.resetCanvasData(sources.base);
		}
	};
	const toggleEraser = () => setIsEraser(!isEraser);

	const selectCanvasThickness = (size) => {
		setBrushRadius(size);
		if(isFront) frontCanvasRef.current.setBrushRadius(size);
		else backCanvasRef.current.setBrushRadius(size);
	};

	const selectColor = useCallback((brushColor) => {
		setBrushColor(brushColor);
		setIsEraser(false);
	}, []);

	const onRequestCloseHandler = useCallback(() => {
		if(isDirtyLocal) setCancelAlertVisible(true);
		else onClose();
	}, [onClose, isDirtyLocal]);

	const onCloseHandler = useCallback(() => {
		if (isLandscape) {
			setAnalysisDataFront(initialDataFront);
			setAnalysisDataBack(initialDataBack);
		}
		onClose();
	}, [isLandscape, onClose, setAnalysisDataFront, initialDataFront, setAnalysisDataBack, initialDataBack]);

	const undo = () => {
		if (isFront) frontCanvasRef.current.undo();
		else backCanvasRef.current.undo();
	};
	const saveHandler = async () => {
		const frontCanvasData = isFront ? frontCanvasRef.current.getDataURL() : frontSource;
		const backCanvasData = !isFront ? backCanvasRef.current.getDataURL() : backSource;
		await save({
			condition,
			frontCanvasData,
			backCanvasData,
			frontData,
			backData,
			tbsaFormula,
			fluidResuscitationFormula,
		});
	};

	const updateHandler = async () => {
		const frontCanvasData = isFront ? frontCanvasRef.current.getDataURL() : frontSource;
		const backCanvasData = !isFront ? backCanvasRef.current.getDataURL() : backSource;
		await update({
			bodySite,
			frontCanvasData,
			backCanvasData,
			frontData,
			backData,
			tbsaFormula,
			fluidResuscitationFormula,
		});
	};

	const toggleSide = useCallback(() => {
		if (isFront) {
			const frontB64 = frontCanvasRef.current.getDataURL();
			setFrontSource(frontB64);
		}
		else {
			const backB64 = backCanvasRef.current.getDataURL();
			setBackSource(backB64);
		}
		setIsFront(!isFront);
	}, [isFront]);

	useEffect(() => {
		const years = getAgeFromEocCondition(eoc, condition);
		setFrontSource(getSilhouette(years, 'front', SILHOUETTE_TYPES.baseB64));
		setBackSource(getSilhouette(years, 'back', SILHOUETTE_TYPES.baseB64));

		if(modalType !== MODAL_TYPES.view && bodySite) {
			const load = async () => {
				const frontB64 = await urlToB64(bodySite.bodySiteFrontB64);
				const backB64 = await urlToB64(bodySite.bodySiteBackB64);
				const frontData = (analysisDataFront) ? analysisDataFront : {
					tbsa: bodySite.tbsaDataFront,
					bodySites: bodySite.bodySitesDataFront,
				};
				const backData = (analysisDataBack) ? analysisDataBack : {
					tbsa: bodySite.tbsaDataBack,
					bodySites: bodySite.bodySitesDataBack,
				};
				setFrontSource(frontB64);
				setBackSource(backB64);
				setFrontData(frontData);
				setBackData(backData);
				setIsLoading(false);
			};
			load();
		}
		else setIsLoading(false);
		return () => {
			if (isNavBar && userType !== USER_TYPES.patient.key) {
				navigation.setOptions({ headerLeft: () => <HeaderLeft onPress={navigation.goBack}/> });
			} else if (isNavBar && userType === USER_TYPES.patient.key) {
				navigation.setOptions({ headerLeft: () => <></> });
			}
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if(!isLoading && isLandscape && setAnalysisDataFront && setAnalysisDataBack) {
			if(isFront) setAnalysisDataFront(frontData);
			else setAnalysisDataBack(backData);
		}
	}, [frontData, backData, isLoading, isFront, setAnalysisDataFront, setAnalysisDataBack, isLandscape]);

	useEffect(() => {
		if(setIsDirty) setIsDirty(isDirtyLocal);
		if (isNavBar) navigation.setOptions({ headerLeft: () => <HeaderLeft onPress={onRequestCloseHandler}/> });
	}, [navigation, isDirtyLocal, setIsDirty, onRequestCloseHandler, isNavBar]);

	useBackHandler(() => {
		if (isDirtyLocal) onRequestCloseHandler();
		return isDirtyLocal ? true : false;
	});

	return (
		<View style={styles.container}>
			<Alert
				alertVisible={cancelAlertVisible}
				setAlertVisible={setCancelAlertVisible}
				title='Discard Body Site Canvas?'
				onSubmit={onCloseHandler}
				onSubmitText={FormText.proceed}
				onCancelText={FormText.back}
				alertType={ALERT_TYPES.confirmCancel}
			/>
			{!isLoading && <View style={{
				height: canvasDims.height + canvasPadding,
				width: (isLandscape) ? canvasDims.width + canvasPadding : layout.width - canvasPadding,
				justifyContent: 'center',
				alignItems: 'center',
				backgroundColor: COLORS.white,
			}}>
				<BodySiteCanvasBack
					dims={canvasDims}
					onCancel={onRequestCloseHandler}
				/>
				<BodySiteCanvasFlipButton
					toggleSide={toggleSide}
					isFront={isFront}
					dims={canvasDims}
					eoc={eoc}
					condition={condition}
					bodySite={bodySite}
				/>

				<BodySiteCanvasLR dims={canvasDims}/>

				{isFront && <CanvasDraw
					ref={frontCanvasRef}
					brushColor={brushColor}
					brushRadius={brushRadius}
					lazyRadius={2}
					canvasWidth={canvasDims.width}
					canvasHeight={canvasDims.height}
					setData={setFrontData}
					canvasData={frontSource}
					overlayData={sources.outline}
					isBodySite
					bodySiteIsFront={true}
					hideGrid
					erase={isEraser}
					isLandscape={isLandscape}
					birthdate={eoc && eoc.birthdate ? DateTime.fromISO(eoc.birthdate) : undefined}
					// injuryDate={condition && condition.onsetDateTime ? DateTime.fromISO(condition.onsetDateTime) : undefined}
					setIsDirty={setIsDirtyLocal}
				/>}
				{!isFront && <CanvasDraw
					ref={backCanvasRef}
					brushColor={brushColor}
					brushRadius={brushRadius}
					lazyRadius={2}
					canvasWidth={canvasDims.width}
					canvasHeight={canvasDims.height}
					setData={setBackData}
					canvasData={backSource}
					overlayData={sources.outline}
					isBodySite
					bodySiteIsFront={false}
					hideGrid
					erase={isEraser}
					isLandscape={isLandscape}
					birthdate={eoc && eoc.birthdate ? DateTime.fromISO(eoc.birthdate) : undefined}
					// injuryDate={condition && condition.onsetDateTime ? DateTime.fromISO(condition.onsetDateTime) : undefined}
					setIsDirty={setIsDirtyLocal}
				/>}
				{frontData && backData && <BodySiteCanvasColorButtonContainer
					selectColor={selectColor}
					frontData={frontData}
					backData={backData}
					brushColor={brushColor}
					isEraser={isEraser}
					dims={canvasDims}
					isLandscape={isLandscape}
				/>}
			</View>}
			{!isLoading && <BodySiteCanvasFooter
				canvasClear={canvasClear}
				toggleEraser={toggleEraser}
				isEraser={isEraser}
				selectCanvasThickness={selectCanvasThickness}
				undo={undo}
				save={modalType === MODAL_TYPES.edit ? updateHandler : saveHandler}
			/>}
			{isLoading && <LoadingWheel/>}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: COLORS.white,
	}
});

export default memo(BodySiteCanvas);